import { useState } from "react";
import DynamicMedia from "src/components/shared/DynamicMedia";
import { ImageAndTextFieldsFragment } from "src/generated/graphql";
import { highlightBracketedText, highlightUnderscoreText } from "src/utils/utils";

type ContentOperationProps = {
  content: ImageAndTextFieldsFragment;
  className?: string;
  omnichannel?: boolean; 
};

const SharedVision = ({ content, className="", omnichannel }: ContentOperationProps) => {
  const videoUrl = content?.image?.url ?? ""
  const imageUrl = content?.backgroundImage?.url ?? ""
  const itemDescription =
    content?.imageDescriptionJson?.json?.content?.[0]?.content?.[0]?.value ?? '';
  const title = content?.title

  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);

  const togglePlayPause = () => {
    const isVideo = videoUrl && (videoUrl.includes('.mp4') || videoUrl.includes('.webm') || videoUrl.includes('.mov'));
    
    if (!isVideo) return;

    if (!showVideo) {
      // First click - show video and start playing
      setShowVideo(true);
      setIsPlaying(true);
      
      // Small timeout to ensure video element is rendered before playing
      setTimeout(() => {
        const videoElement = document.querySelector('.shared-vision-video video') as HTMLVideoElement;
        if (videoElement) {
          videoElement.play();
        }
      }, 10);
    } else {
      // Video is already showing - toggle play/pause
      const videoElement = document.querySelector('.shared-vision-video video') as HTMLVideoElement;
      
      if (videoElement) {
        if (isPlaying) {
          videoElement.pause();
          setIsPlaying(false);
        } else {
          videoElement.play();
          setIsPlaying(true);
        }
      }
    }
  };

  const isVideo = videoUrl && (videoUrl.includes('.mp4') || videoUrl.includes('.webm') || videoUrl.includes('.mov'));
  // Use imageUrl as thumbnail initially, then switch to video when playing
  const mediaSrc = isVideo && !showVideo ? imageUrl : videoUrl;

  return (
      <div className={`sitecore-shared-vision ${className}`}>
        <div className="container-fluid custom-spacer-col position-relative gap-5">
          <div
            className="product-content-wrapper"
            style={{ minHeight: "500px", marginTop: "200px" }}
          >
            <h1
              className="product-title product-title-custom light mx-w-770 text-center mx-auto"
              dangerouslySetInnerHTML={{
                __html: highlightBracketedText(title ?? "",  "font-medium"),
              }}
            />
            <div className="mx-w-770 text-center mx-auto">
              <p className="product-description product-inner-description text-center regular" 
                  dangerouslySetInnerHTML={{
                  __html: omnichannel?  highlightUnderscoreText(itemDescription ?? "") : highlightBracketedText(itemDescription ?? "" , "bold"),
                }}
              />
            </div>
            <div>
              <div className="shared-vision-video" style={{ position: "relative" }}>
                <DynamicMedia
                  src={mediaSrc ?? ""}
                  alt={content?.image?.title ?? ""}
                  ariaLabel={content?.image?.title ?? ""}
                  videoAutoplay={false}
                  lottieProps={{
                    loop: true,
                    autoplay: false,
                    style: {
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      transform: "unset",
                    },
                  }}
                  lottieClassName="feature-lottie"
                />
                
                {/* Play/Pause Button for Videos */}
                {isVideo && (
                  <button
                    onClick={togglePlayPause}
                    className="video-detail-play-pause-btn"
                  >
                    {showVideo && isPlaying ? (
                      // Pause icon - when video is playing
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M3 1V11"
                          stroke="black"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <path
                          d="M9 1V11"
                          stroke="black"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    ) : (
                      // Play icon - when showing thumbnail or video is paused
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M3 1L10 6L3 11V1Z" fill="black" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
  );
};

export default SharedVision;